import React from 'react';
import '../styles/ConfirmDialog.css';

/**
 * Small reusable confirmation modal — replaces window.confirm() for any
 * UI that needs a styled, accessible yes/no prompt. Stack-aware: renders
 * above other modals via a higher z-index.
 *
 * Props:
 *   open         — boolean
 *   title        — short header text
 *   message      — body copy (string or node)
 *   confirmLabel — primary button text (default "Confirm")
 *   cancelLabel  — secondary button text (default "Cancel")
 *   variant      — 'danger' | 'primary' (default 'primary'); affects confirm button color
 *   onConfirm    — fired when user clicks confirm
 *   onCancel     — fired on cancel, backdrop click, or Esc
 */
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  onConfirm,
  onCancel
}) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter') onConfirm?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel} role="dialog" aria-modal="true">
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        {title && <h3 className="confirm-dialog-title">{title}</h3>}
        <div className="confirm-dialog-body">{message}</div>
        <div className="confirm-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={variant === 'danger' ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
